import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { canonicalFromLocation } from '../utils/canonicalUrl';

const { FiCalendar, FiUser, FiArrowLeft, FiShare2, FiFacebook, FiTwitter, FiLinkedin } = FiIcons;

const BlogPost = () => {
  const { slug } = useParams();

  // Blog posts data
  const blogPosts = {};

  // Get the current blog post based on slug
  const blogPost = blogPosts[slug];

  // Get actual blog posts from the Blog page to show as related
  const allBlogPosts = [
    {
      id: 1,
      title: 'Porsche PSM Failure: A Mechanic\'s Guide',
      slug: 'porsche-psm-failure-guide',
      image: '/assets/assets/images/business-shop-sign.webp',
      date: '2025-01-07',
    },
    {
      id: 2,
      title: 'VW EPC Light On? How to fix your volkswagen EPC light!',
      slug: 'vw-epc-light-fix-guide',
      image: '/assets/assets/images/business-shop-sign.webp',
      date: '2025-01-07',
    },
  ];
  
  // Filter out the current post from related posts
  const relatedPosts = allBlogPosts.filter(post => post.slug !== slug);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = canonicalFromLocation();
  const shareTitle = blogPost.title;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50"
    >
      <Helmet>
        <title>{blogPost.title} | Paradise-Performance</title>
        <meta name="description" content={blogPost.metaDescription || blogPost.excerpt} />
        <link rel="canonical" href={shareUrl} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={blogPost.title} />
        <meta property="og:description" content={blogPost.metaDescription || blogPost.excerpt} />
        <meta property="og:image" content={blogPost.image} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={blogPost.date} />
        <meta property="article:author" content={blogPost.author} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogPost.title} />
        <meta name="twitter:description" content={blogPost.metaDescription || blogPost.excerpt} />
        <meta name="twitter:image" content={blogPost.image} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogPost.title,
            "description": blogPost.metaDescription || blogPost.excerpt,
            "image": blogPost.image,
            "datePublished": blogPost.date,
            "author": {
              "@type": "Organization",
              "name": blogPost.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Paradise-Performance",
              "logo": {
                "@type": "ImageObject",
                "url": "https://paradise-performance.com/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            <span>Back to Blog</span>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Featured Image */}
              <div className="h-64 md:h-96">
                <img
                  src={blogPost.image}
                  alt={blogPost.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div className="p-8">
                {/* Meta Information */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                  <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full font-medium">
                    {blogPost.category}
                  </span>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>{formatDate(blogPost.date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiUser} className="w-4 h-4" />
                    <span>{blogPost.author}</span>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  {blogPost.title}
                </h1>

                {/* Social Share */}
                <div className="flex items-center space-x-4 mb-8 pb-6 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Share:</span>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiFacebook} className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-500 transition-colors"
                  >
                    <SafeIcon icon={FiTwitter} className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-800 hover:text-blue-900 transition-colors"
                  >
                    <SafeIcon icon={FiLinkedin} className="w-5 h-5" />
                  </a>
                </div>

                {/* Article Body */}
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: blogPost.content }}
                />

                {/* Call to Action */}
                <div className="mt-12 p-6 bg-primary-50 rounded-lg border border-primary-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Ready to Find Your Perfect Vehicle?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Our expert team is here to help you choose the right vehicle for your needs and budget. 
                    Visit our showroom or browse our extensive inventory online.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/inventory"
                      className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Browse Inventory
                    </Link>
                    <Link
                      to="/contact"
                      className="border border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Schedule Test Drive
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Posts */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Related Articles</h3>
              <div className="space-y-6">
                {relatedPosts.map((post) => (
                  <article key={post.id} className="flex space-x-4">
                    <div className="w-20 h-20 flex-shrink-0">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="hover:text-primary-600 transition-colors"
                        >
                          {post.title}
                        </Link>
                      </h4>
                      <p className="text-xs text-gray-500">
                        {formatDate(post.date)}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-primary-600 text-white rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">Stay Informed</h3>
              <p className="text-primary-200 mb-6 text-sm">
                Get the latest automotive tips, reviews, and industry news delivered to your inbox.
              </p>
              <form className="space-y-4">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="w-full px-4 py-3 rounded-lg text-gray-900"
                />
                <button
                  type="submit"
                  className="w-full bg-white text-primary-900 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPost;